import { Accordion } from '@mantine/core'
import clsx from 'clsx'
import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import { Button } from 'src/components/Button/Button'
import { CoffeeDetailsTable } from 'src/components/CoffeeDetailsTable/CoffeeDetailsTable'
import { Modal } from 'src/components/Modal/Modal'
import { CoffeeFoundationElement } from 'src/components/ProductAccordion/CoffeeFoundationElement'
import { GearExtendedElementInfo } from 'src/components/ProductAccordion/GearExtendedInfoElement'
import { GearInfoElement } from 'src/components/ProductAccordion/GearInfoElement'
import { GearReturnsElement } from 'src/components/ProductAccordion/GearReturnsElement'
import { Text } from 'src/components/Text/Text'
import { config } from 'src/config'
import { useLocale } from 'src/contexts/LocaleContext'
import { removeBioOrganic } from 'src/functions/product/removeBioOrganic'
import { getMetafield, getMetafields, getMetaobjectValueFromStored } from 'src/functions/util/getMetafield'

import styles from './ProductAccordion.module.scss'

import type { Product, Metafield, ProductVariant } from 'src/data/graphql-generated'

interface ProductAccordionProps {
  product: Product
  variant: ProductVariant
}

export const ProductAccordion = ({ product, variant }: ProductAccordionProps) => {
  const { locale } = useLocale()
  const { t } = useTranslation()
  const [showShippingTermsModal, setShowShippingTermsModal] = useState(false)
  const { metafields, title, productType } = product

  // * Coffee Metafields
  const coffeeMetafieldsRaw = getMetafields(metafields, 'coffee', locale)
  const coffeeTaste = getMetafield(metafields, 'coffee', 'taste', locale)
    ?.map((taste: Record<string, string>) => taste?.title)
    .join(', ')
  const coffeeVariety = getMetafield(metafields, 'coffee', 'variety', locale)
    ?.map((taste: Record<string, string>) => taste?.title)
    .join(', ')
  const coffeeSpecies = getMetafield(metafields, 'coffee', 'species', locale)
    ?.map((taste: Record<string, string>) => taste?.title)
    .join(', ')
  const coffeeCharacterAdjective = getMetafield(metafields, 'coffee', 'character_adjective', locale)?.title
  const coffeeMetafields = coffeeMetafieldsRaw.length
    ? [
        coffeeTaste && {
          namespace: 'coffee',
          key: 'taste',
          value: `${coffeeCharacterAdjective ? `${coffeeCharacterAdjective} | ` : ''}${coffeeTaste}`,
        },
        ...coffeeMetafieldsRaw.filter((metafield) => !['character_adjective', 'taste'].includes(metafield.key)),
        coffeeSpecies && {
          namespace: 'coffee',
          // this key is then matched from the translations to get the title for the coffee details table
          key: 'composed_variety',
          value: coffeeVariety ? `${coffeeSpecies} | ${coffeeVariety}` : coffeeSpecies,
        },
      ]
    : []

  // * Gear Metafields
  const gearInfoLabelIds = getMetafield(variant.metafields, 'gear', 'information_labels', locale)
  const gearInfoLabels = gearInfoLabelIds?.map((label: { id: string }) =>
    getMetaobjectValueFromStored(label.id, locale),
  )
  const gearInfoValues = getMetafield(variant.metafields, 'gear', 'information_values', locale)

  const gearExtendedLabelIds = getMetafield(variant.metafields, 'gear', 'extended_labels', locale)
  const gearExtendedLabels = gearExtendedLabelIds?.map((label: { id: string }) =>
    getMetaobjectValueFromStored(label.id, locale),
  )
  const gearExtendedValues = getMetafield(variant.metafields, 'gear', 'extended_values', locale)

  // * Delivery Metafield
  const deliveryTimeOverride = getMetafield(variant.metafields, 'product', 'delivery_time')
  const [min, max] = (deliveryTimeOverride || config.deliveryTime).split('-')

  const deliveryElement = {
    title: t('pdp.accordion.common.delivery.title'),
    element: (
      <Text variant="p">
        <Trans
          i18nKey="pdp.accordion.common.delivery.text"
          values={{ min, max }}
          components={[
            <Button
              key="shipping-modal"
              onClick={() => setShowShippingTermsModal(true)}
              variant="ghost"
              inline
              // align the button with the text
              style={{ marginTop: '-0.13rem' }}
            />,
          ]}
        />
      </Text>
    ),
  }

  const coffeeAccordionElements = [
    coffeeMetafields.length > 0 && {
      title: `${removeBioOrganic(title)} ${t('pdp.accordion.coffee.coffeeDetails.title')}`,
      element: <CoffeeDetailsTable details={coffeeMetafields as Metafield[]} />,
    },
    {
      title: t('pdp.accordion.coffee.coffeeFoundation.title'),
      element: <CoffeeFoundationElement />,
    },
    deliveryElement,
  ]

  const gearAccordionElements = [
    gearInfoLabels && {
      title: t('pdp.accordion.gear.gearInformation.title'),
      element: <GearInfoElement gearInfo={{ labels: gearInfoLabels, values: gearInfoValues }} />,
    },
    deliveryElement,
    {
      title: t('pdp.accordion.gear.returns.title'),
      element: <GearReturnsElement />,
    },
    gearExtendedLabels && {
      title: t('pdp.accordion.gear.gearExtended.title'),
      element: <GearExtendedElementInfo gearExtended={{ labels: gearExtendedLabels, values: gearExtendedValues }} />,
    },
  ]

  const isCoffee = productType === 'coffee'
  const elements = (isCoffee ? coffeeAccordionElements : gearAccordionElements).filter((i) => i)

  return (
    <div className={styles.container}>
      <Accordion
        radius="0"
        // * no default open accordions for gear
        defaultValue={isCoffee && elements[0]?.title}
        mt="lg"
        classNames={{ content: styles.content, label: styles.label, item: styles.item }}
      >
        {elements.map((item, index) => (
          <Accordion.Item key={item.title} value={item.title}>
            <Accordion.Control classNames={{ control: clsx(styles.control, index === 0 && styles.first) }}>
              {item.title}
            </Accordion.Control>
            <Accordion.Panel>{item.element}</Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>

      <Modal
        title={t('common.shippingTermsModal.title')}
        opened={showShippingTermsModal}
        onClose={() => setShowShippingTermsModal(false)}
      >
        <div dangerouslySetInnerHTML={{ __html: t('common.shippingTermsModal.body') }} suppressHydrationWarning />
      </Modal>
    </div>
  )
}
